<template>
  <div>
    <transition name="fade">
      <MenssageAlert :messages="messages" />
    </transition>
    <div
      class="drop-area"
      @dragover.prevent="handleDragOver"
      @dragleave="handleDragLeave"
      @drop.prevent="handleDrop"
      @click="handleClick"
      :class="{ dragover: isDragOver, disabled: disabled }"
    >
      <input type="file" ref="fileInput" @change="onFileSelect" />
      <div v-if="files.length === 0">
        <CIcon name="cilCloudUpload" :height="40" viewBox="0 0 24 24" />
        <p class="mb-0 mt-2">{{ description }}</p>
      </div>

      <div v-else class="file-details">
        <ul>
          <li v-for="file in files" :key="file.name">
            <span class="font-weight-bold">{{ file.name }}</span>
            <CIcon class="ml-2" name="cilFile" :height="20" />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import MenssageAlert from '@/components/MenssageAlert'
import { formatFileSize } from '@/utils/numbersFormatUtil.js'

export default {
  components: { MenssageAlert },
  props: {
    description: {
      type: String,
      required: true,
    },
    maxFileSizeBytes: {
      type: Number, // Tamanho máximo em bytes
      default: null,
    },
    acceptedFileTypes: {
      type: Array, // Array de MIME types (ex: ['image/jpeg', 'image/png']
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDragOver: false,
      files: [],
      messages: [],
      mimeToExtensionMap: {
        'application/vnd.ms-excel': '.csv',
        'text/csv': '.csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
      },
    }
  },
  computed: {
    formattedAcceptedFileTypes() {
      if (this.acceptedFileTypes.length === 0) return ''

      const typesArray = this.acceptedFileTypes.toString().split(',')

      const formattedTypes = typesArray.map((type) => {
        return this.mimeToExtensionMap[type] || type.split('/')[1].toUpperCase()
      })

      const uniqueFormattedTypes = [...new Set(formattedTypes)]

      return uniqueFormattedTypes.join(', ')
    },
  },
  methods: {
    // * Chamado pelo componente pai para limpar o componente
    clearFiles() {
      this.files = []
      this.messages = []
      this.$refs.fileInput.value = ''
    },
    handleDragOver() {
      if (this.disabled) return
      this.isDragOver = true
    },
    handleDragLeave() {
      if (this.disabled) return
      this.isDragOver = false
    },
    handleDrop(event) {
      if (this.disabled) return
      const droppedFiles = Array.from(event.dataTransfer.files)
      this.validateFiles(droppedFiles)
      this.isDragOver = false
    },
    handleClick() {
      this.$refs.fileInput.click()
    },
    onFileSelect(event) {
      if (this.disabled) return
      const files = event.target.files
      this.validateFiles(files)
    },
    validateFiles(files) {
      const file = files[0]

      // Check file size
      if (this.maxFileSizeBytes && file.size > this.maxFileSizeBytes) {
        this.messages = [
          {
            status: 'danger',
            text: this.$t('drop-file-area.errors.exceed-limit', [formatFileSize(this.maxFileSizeBytes)]),
          },
        ]
        this.files = []
        return
      }

      // Normalize MIME type checking
      const isValidType = this.acceptedFileTypes.some((type) => file.type.includes(type) || file.name.endsWith(this.mimeToExtensionMap[type]))

      if (!isValidType) {
        this.messages = [
          {
            status: 'danger',
            text: this.$t('drop-file-area.errors.invalid-type', [this.formattedAcceptedFileTypes]),
          },
        ]
        this.files = []
        return
      }

      this.files = [file]
      this.$emit('files-selected', file)
    },
  },
}
</script>

<style lang="scss" scoped>
.drop-area {
  border: 1px solid var(--gray-300);
  border-radius: 5px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  color: var(--primary);
  font-size: 16px;
  background-color: var(--white);
  transition: background-color 0.3s ease;

  &:hover {
    background-color: var(--gray-200);
  }
}

.drop-area.disabled {
  background-color: var(--gray-200);
  cursor: not-allowed;
  opacity: 0.6;
}

.drop-area.dragover {
  background-color: var(--gray-200);
  border: 1px solid var(--gray-200);
}

.drop-area input {
  display: none;
}

.file-details {
  margin-top: 20px;
  font-size: 16px;
}

.file-details ul {
  list-style-type: none;
  padding-left: 0;
}

.file-details li {
  margin-bottom: 10px;
}
</style>
